import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';

import Helmet from 'react-helmet';

import Layout, { ContentSection } from '~components/Layout';
import Register from '~components/Register';
import StateRegisterInfo from '~components/common/StateRegisterInfo';
import StateContactInfo from '~components/common/StateContactInfo';
import StateExternalLinks from '~components/common/StateExternalLinks';
import Distraction from '~components/common/Distraction';

import states from '~utils/states';
import { indexStateDataBySlug } from '~utils/indexStateData';
import StatesListSection from '../components/StatesList/StatesListSection';
import StateAlerts from '../components/common/StateAlerts';

function StateRegistrationPage({ data }) {
  const state = data.voteAmericaState;
  const title = `Register to Vote in ${state.name} - VoteAmerica`;
  const description = `${state.name} voter registration. Register to vote securely today. It takes less than 2 minutes to register to vote.`;

  const info = indexStateDataBySlug(state);

  const statesListData = states.map(({ stateName, stateSlug }) => ({
    key: stateSlug,
    linkText: `${stateName} voter registration`,
    linkUrl: `/register-to-vote/${stateSlug}/`,
  }));

  const initialh1 = `Register to vote or update your registration in ${state.name}`;

  return (
    <Layout
      title={title}
      description={description}
      image='share/v6/tools/register.png'
    >
      <Helmet>
        <script type='application/ld+json'>
          {`
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [{
                "@type": "ListItem",
                "position": 1,
                "name": "Voter registration",
                "item": "https://www.voteamerica.org/register-to-vote"
              },{
                "@type": "ListItem",
                "position": 2,
                "name": "${state.name} voter registration"
              }]
            }
          `}
        </script>
      </Helmet>

      <ContentSection>
        <StateAlerts stateInfo={info} showRegistrationAlerts />
        <Register initialh1={initialh1} description={description} />
      </ContentSection>

      <Distraction>
        <StateRegisterInfo info={info} state={state} showLastUpdated />
        <StateContactInfo state={state} info={info} />
        <StateExternalLinks state={state} info={info} />
        <StatesListSection data={statesListData} heading='Voter registration' />
      </Distraction>
    </Layout>
  );
}

StateRegistrationPage.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.object,
    voteAmericaState: PropTypes.shape({
      name: PropTypes.string,
      code: PropTypes.string,
      state_information: PropTypes.array,
    }),
  }),
};

export default StateRegistrationPage;

export const pageQuery = graphql`
  query StateRegistrationInformation($id: String) {
    voteAmericaState(code: { eq: $id }) {
      name
      code
      state_information {
        html
        text
        bool_value
        multiselect_value
        footnotes
        modified_at
        field {
          slug
        }
      }
    }
  }
`;
